import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useSavedDecksQuery,
  useDeleteSavedDeckMutation,
} from '@/services/qbotApi';
import type { SavedDeck } from '@/types/qbot';
import { cn } from '@/lib/cn';

/**
 * List of decks the user has saved against their Logto identity.
 * Cards are stored as plain card-name strings (not full CRPlayerCard
 * objects) so we render them as chips rather than DeckGrid — the visual
 * deck grid lives on PopularDecks / DeckCheck where the server already
 * returns full card objects.
 *
 * Delete is destructive; we route the click through a small confirm modal.
 */
export function CRSavedDecks() {
  const { t } = useTranslation('cr');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { data, isLoading, error } = useSavedDecksQuery();
  const [del, { isLoading: deleting }] = useDeleteSavedDeckMutation();
  const [confirmId, setConfirmId] = useState<number | null>(null);

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('saved.loading')}</div>;
  }
  if (error || !data) {
    return <div className="px-4 py-6 text-sm text-rose-300">{t('saved.error')}</div>;
  }

  const decks = data.decks;

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 py-4">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-bold text-fg">{t('saved.title')}</h1>
        <span className="text-xs text-fg-muted tabular-nums">
          {decks.length}/50
        </span>
      </header>

      {decks.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm text-fg-muted">{t('saved.empty')}</p>
          <button
            type="button"
            onClick={() => {
              haptics.impact('light');
              navigate('/cr/decks/popular');
            }}
            className="rounded-lg bg-brand px-4 py-2 text-xs font-bold uppercase tracking-wide text-brand-fg"
          >
            <Plus aria-hidden className="inline h-4 w-4 -mt-0.5 mr-1" />
            {t('saved.cta')}
          </button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {decks.map((deck) => (
            <SavedDeckRow
              key={deck.id}
              deck={deck}
              confirming={confirmId === deck.id}
              deleting={deleting}
              onConfirmRequest={() => setConfirmId(deck.id)}
              onConfirmCancel={() => setConfirmId(null)}
              onConfirmDelete={async () => {
                try {
                  await del({ id: deck.id }).unwrap();
                  haptics.notification('success');
                } catch {
                  haptics.notification('error');
                } finally {
                  setConfirmId(null);
                }
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface RowProps {
  deck: SavedDeck;
  confirming: boolean;
  deleting: boolean;
  onConfirmRequest: () => void;
  onConfirmCancel: () => void;
  onConfirmDelete: () => void;
}

function SavedDeckRow({
  deck,
  confirming,
  deleting,
  onConfirmRequest,
  onConfirmCancel,
  onConfirmDelete,
}: RowProps) {
  const { t } = useTranslation('cr');
  return (
    <li className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-semibold text-fg">{deck.name}</p>
          <p className="text-[11px] text-fg-muted">{deck.archetype}</p>
        </div>
        {!confirming ? (
          <button
            type="button"
            onClick={onConfirmRequest}
            aria-label={t('saved.row.deleteAria')}
            className="rounded-md p-1.5 text-fg-muted transition-colors active:text-rose-400"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onConfirmCancel}
              disabled={deleting}
              className="rounded-md bg-bg-tertiary px-3 py-1 text-[11px] font-semibold text-fg-muted"
            >
              {t('saved.row.cancel')}
            </button>
            <button
              type="button"
              onClick={onConfirmDelete}
              disabled={deleting}
              className={cn(
                'rounded-md bg-rose-500/20 px-3 py-1 text-[11px] font-semibold text-rose-300',
                deleting && 'opacity-50',
              )}
            >
              {deleting ? t('saved.row.deleting') : t('saved.row.confirm')}
            </button>
          </div>
        )}
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {deck.cards.map((c) => (
          <li
            key={c}
            className="rounded-md bg-bg-tertiary px-2 py-0.5 text-[11px] text-fg-muted"
          >
            {c}
          </li>
        ))}
      </ul>
    </li>
  );
}
